import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Terminal, Globe, Building, CheckCircle, Loader, Shield, Lock, Zap } from 'lucide-react';

const TransferProcessingAnimation = ({ 
  transferType, 
  transferData, 
  onComplete 
}) => {
  const [phase, setPhase] = useState(0);
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState(0);

  const processingSteps = {
    'Terminal': [
      { text: 'Connecting to Terminal...', duration: 800 },
      { text: 'Validating Terminal ID...', duration: 600 },
      { text: 'Verifying Location...', duration: 700 },
      { text: 'Checking Authorization Code...', duration: 900 },
      { text: 'Validating PIN...', duration: 500 },
      { text: 'Processing Transaction...', duration: 1200 },
      { text: 'Generating Receipt...', duration: 400 },
      { text: 'Transaction Complete', duration: 300 }
    ],
    'S2S/SWIFT': [
      { text: 'Initializing SWIFT Network...', duration: 1000 },
      { text: 'Validating SWIFT/BIC Code...', duration: 800 },
      { text: 'Verifying IBAN...', duration: 700 },
      { text: 'Checking Beneficiary Bank...', duration: 900 },
      { text: 'Compliance Check (AML/KYC)...', duration: 1200 },
      { text: 'Currency Conversion...', duration: 600 },
      { text: 'Routing through SWIFT...', duration: 1500 },
      { text: 'Confirming with Correspondent Bank...', duration: 1000 },
      { text: 'Transaction Confirmed', duration: 400 }
    ],
    'S2S/SEPA': [
      { text: 'Connecting to SEPA Network...', duration: 900 },
      { text: 'Validating IBAN Format...', duration: 600 },
      { text: 'Checking BIC Code...', duration: 700 },
      { text: 'Verifying SEPA Zone...', duration: 500 },
      { text: 'Processing SEPA Credit Transfer...', duration: 1200 },
      { text: 'Generating SEPA XML...', duration: 400 },
      { text: 'Submitting to EBA Clearing...', duration: 1000 },
      { text: 'Transaction Accepted', duration: 300 }
    ],
    'S2S/INTERNAL': [
      { text: 'Validating Internal Account...', duration: 600 },
      { text: 'Checking Department Code...', duration: 500 },
      { text: 'Verifying Access Rights...', duration: 700 },
      { text: 'Processing Internal Transfer...', duration: 800 },
      { text: 'Updating Ledger...', duration: 600 },
      { text: 'Transaction Complete', duration: 300 }
    ],
    'S2S/EXTERNAL': [
      { text: 'See S2S Connection Animation', duration: 500 }
    ]
  };

  const steps = processingSteps[transferType] || processingSteps['Terminal'];

  useEffect(() => {
    let currentStep = 0;
    
    const processStep = () => {
      if (currentStep < steps.length) {
        const step = steps[currentStep];
        
        setLogs(prev => [...prev, {
          time: new Date().toLocaleTimeString(),
          message: step.text,
          status: currentStep === steps.length - 1 ? 'success' : 'processing'
        }]);
        
        setProgress(((currentStep + 1) / steps.length) * 100);
        currentStep++;
        
        setTimeout(processStep, step.duration);
      } else {
        setTimeout(() => {
          onComplete(true);
        }, 500);
      }
    };
    
    processStep();
  }, []);

  const getIcon = () => {
    switch(transferType) {
      case 'Terminal': return Terminal;
      case 'S2S/SWIFT': return Globe;
      case 'S2S/SEPA': return Building;
      default: return Terminal;
    }
  };

  const Icon = getIcon();

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
      <div className="scanline" />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-3xl terminal-card p-8"
      >
        {/* Header */}
        <div className="text-center mb-6">
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity 
            }}
            className="inline-block"
          >
            <Icon className="w-16 h-16 mx-auto mb-4 text-terminal-green" />
          </motion.div>
          <h2 className="text-2xl font-bold cyber-font glow-text mb-2">
            PROCESSING {transferType} TRANSFER
          </h2>
          <p className="text-terminal-green-dim text-sm">
            Amount: ${transferData.amount} {transferData.currency}
          </p>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-xs mb-2">
            <span className="text-terminal-green-dim">Status</span>
            <span className="text-terminal-green font-mono">{progress.toFixed(0)}%</span>
          </div>
          <div className="h-3 bg-gray-800 rounded-full overflow-hidden relative">
            <motion.div
              className="h-full bg-gradient-to-r from-terminal-green to-green-400"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              style={{ 
                boxShadow: '0 0 20px rgba(0, 255, 65, 0.8)',
                filter: 'brightness(1.2)'
              }}
            />
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              style={{ width: '50%' }}
            />
          </div>
        </div>

        {/* Transfer Details */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="terminal-card p-3 border border-terminal-green/30">
            <div className="text-xs text-terminal-green-dim mb-1">FROM</div>
            <div className="font-mono text-xs">{transferData.from_account}</div>
          </div>
          <div className="terminal-card p-3 border border-terminal-green/30">
            <div className="text-xs text-terminal-green-dim mb-1">TO</div>
            <div className="font-mono text-xs truncate">{transferData.recipient_info}</div>
          </div>
        </div>

        {/* Processing Logs */}
        <div className="terminal-card p-4 h-48 overflow-y-auto terminal-font text-xs space-y-1">
          {logs.map((log, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-2"
            >
              {log.status === 'success' ? (
                <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" />
              ) : (
                <Loader className="w-3 h-3 text-terminal-green animate-spin flex-shrink-0" />
              )}
              <span className="text-gray-500">[{log.time}]</span>
              <span className={log.status === 'success' ? 'text-green-400' : 'text-terminal-green'}>
                {log.message}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Security Indicators */}
        <div className="mt-6 flex items-center justify-center gap-6 text-xs">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-green-400" />
            <span className="text-green-400">ENCRYPTED</span>
          </div>
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-green-400" />
            <span className="text-green-400">SECURE</span>
          </div>
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-400">FAST</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TransferProcessingAnimation;
